// OBT F3-C — Tek kampanya analitiği: cevap dağılımı + revizyon + süre + karar.
import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtStatTile, ObtStatusBadge } from '../ObtStates';
import {
  CAMPAIGN_STATUS_LABELS,
  RESPONSE_STATUS_LABELS,
  REVIEW_STATE_LABELS,
  obtLabel,
} from '../obtEnums';
import ReportChart from './ReportChart';
import { useCampaignAnalytics } from './hooks/useCampaignAnalytics';
import {
  averageResponseSeconds,
  revisionCountFromSummary,
  rejectedCountFromSummary,
  suggestedCountFromSummary,
} from './reportMetrics';
import { formatDuration, DURATION_STAGE_LABELS } from './reportFormat';

export default function CampaignAnalyticsPanel({ campaign, enabled = true, onClose }) {
  const campaignId = campaign?.campaign_id || null;
  const { loading, error, summary, responseSummary, durations, decisionHistory, reload } =
    useCampaignAnalytics(campaignId, { enabled: enabled && Boolean(campaignId) });

  const responseDist = useMemo(() => {
    const by = responseSummary?.by_status || {};
    return Object.entries(by).map(([k, v]) => ({
      name: obtLabel(RESPONSE_STATUS_LABELS, k),
      value: Number(v) || 0,
    }));
  }, [responseSummary]);

  const reviewDist = useMemo(() => {
    const by = responseSummary?.by_review_state || {};
    return Object.entries(by).map(([k, v]) => ({
      name: k === 'not_started' ? 'İnceleme başlamadı' : obtLabel(REVIEW_STATE_LABELS, k),
      value: Number(v) || 0,
    }));
  }, [responseSummary]);

  const avgSeconds = averageResponseSeconds(durations);
  const revisionCount = revisionCountFromSummary(responseSummary);
  const rejectedCount = rejectedCountFromSummary(responseSummary);
  const suggestedCount = suggestedCountFromSummary(responseSummary);
  const latestDecision = decisionHistory?.versions?.[0] || null;

  return (
    <Card className="border-blue-200">
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">
              {campaign?.title || 'Bilgi Talebi'}
            </h3>
            {summary && (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <ObtStatusBadge value={summary.status} labelMap={CAMPAIGN_STATUS_LABELS} />
                {summary.topic && <span>Konu: {summary.topic}</span>}
              </div>
            )}
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Detayı kapat">
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>

        {loading && <ObtLoading label="Analiz yükleniyor…" />}
        {!loading && error && <ObtErrorState error={error} onRetry={reload} />}

        {!loading && !error && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <ObtStatTile label="Toplam cevap" value={responseSummary?.total ?? 0} tone="info" />
              <ObtStatTile label="Revizyon (istek)" value={revisionCount} tone="warning" />
              <ObtStatTile label="Reddedilen" value={rejectedCount} tone="danger" />
              <ObtStatTile label="Onaylı öneri park" value={suggestedCount} tone="neutral" />
              <ObtStatTile label="Ort. cevap süresi" value={formatDuration(avgSeconds)} tone="neutral" />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ReportChart title="Cevap durum dağılımı" data={responseDist} valueFormatter={(v) => `${v} cevap`} />
              <ReportChart title="İnceleme durumu dağılımı" data={reviewDist} valueFormatter={(v) => `${v} cevap`} />
            </div>

            {/* İnceleme süreleri (varsa) */}
            {durations?.stages?.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Aşama</th>
                      <th className="px-3 py-2">Ortalama</th>
                      <th className="px-3 py-2">En kısa</th>
                      <th className="px-3 py-2">En uzun</th>
                      <th className="px-3 py-2">Örneklem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {durations.stages.map((s) => (
                      <tr key={s.stage} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-800">
                          {DURATION_STAGE_LABELS[s.stage] || s.stage}
                        </td>
                        <td className="px-3 py-2">{formatDuration(s.average_seconds)}</td>
                        <td className="px-3 py-2">{formatDuration(s.min_seconds)}</td>
                        <td className="px-3 py-2">{formatDuration(s.max_seconds)}</td>
                        <td className="px-3 py-2 text-gray-500">{s.sample_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {latestDecision && (
              <p className="text-xs text-gray-600">
                Nihai karar sürümü: <span className="font-semibold">{latestDecision.version_number}</span>
                {' · '}
                {obtLabel(
                  { draft: 'Taslak', finalized: 'Kesinleşti', superseded: 'Yerine yenisi geçti' },
                  latestDecision.status
                )}
                {' · seçilen park: '}
                {latestDecision.summary?.selected ?? 0}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
